import { useQuery, queryOptions } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';

import { geocodeLocation } from '../../api/geocodeApi';
import { fetchHrMatchCount } from '../../api/matchCountApi';
import { fetchProspectQueries } from '../../api/prospectQueriesApi';
import { resolveIndustryTerms } from '../../api/resolveIndustryApi';

import { prospectQueryKeys } from './keys';

// New unified hook for filtered prospect queries with optional pagination
export const prospectQueriesFilteredOptions = (queryTypeCodes = null, page = null, limit = null) =>
	queryOptions({
		queryKey: prospectQueryKeys.filtered(queryTypeCodes, page, limit),
		queryFn: async () => {
			const result = await fetchProspectQueries({ queryTypeCodes, page, limit });
			if (result.error) throw result.error;
			return result;
		},
		staleTime: STALE_TIMES.STANDARD,
	});

export function useProspectQueriesFiltered(queryTypeCodes = null, page = null, limit = null) {
	return useQuery(prospectQueriesFilteredOptions(queryTypeCodes, page, limit));
}

// Specific hooks for different use cases
export function useB2bHeroLeadSearchQueries() {
	return useProspectQueriesFiltered([
		'b2bheroLeadSearch',
		'b2bheroCommercialRegisterSearch',
		'csvImport',
		'agentCuratedSearch',
	]);
}

/**
 * Live HR match-count preview for the search popup ("Treffer-Trichter").
 *
 * Pass the (already debounced) backend-shaped search parameters and an `enabled`
 * flag — the caller is responsible for enabling this only for HR searches with at
 * least one active criterion. Results are short-lived (FREQUENT) so the preview
 * stays fresh while editing without hammering Typesense on every keystroke.
 *
 * @param {Object|null} searchParameters - Backend HR search params, or null while disabled
 * @param {boolean} enabled - Whether the count should be fetched
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useHrMatchCount(searchParameters, enabled) {
	return useQuery({
		queryKey: prospectQueryKeys.hrMatchCount(searchParameters),
		queryFn: () => fetchHrMatchCount(searchParameters),
		enabled: Boolean(enabled && searchParameters),
		staleTime: STALE_TIMES.FREQUENT,
		meta: { suppressGlobalError: true },
	});
}

/**
 * Query options for resolving free-text industry terms to WZ-2025 codes/prefixes.
 * Shared by the live preview hook and the search-start flow (via
 * queryClient.fetchQuery) so the ~1-3s resolver roundtrip is paid once per unique
 * term set. Resolution for identical terms is stable within a session → STATIC.
 *
 * @param {string[]} industrySearchTerms - Parsed, trimmed free-text terms
 * @returns {import('@tanstack/react-query').UseQueryOptions}
 */
export const resolveIndustryOptions = (industrySearchTerms) =>
	queryOptions({
		queryKey: prospectQueryKeys.resolveIndustry(industrySearchTerms),
		queryFn: () => resolveIndustryTerms(industrySearchTerms),
		staleTime: STALE_TIMES.STATIC,
		meta: { suppressGlobalError: true },
	});

/**
 * Resolves (already debounced) free-text industry terms to WZ codes for the
 * live match-count preview.
 *
 * @param {string[]|null} industrySearchTerms - Parsed terms, or null while disabled
 * @param {boolean} enabled - Whether resolution should run
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useResolveIndustry(industrySearchTerms, enabled) {
	return useQuery({
		...resolveIndustryOptions(industrySearchTerms || []),
		enabled: Boolean(enabled && industrySearchTerms?.length > 0),
	});
}

/**
 * Geocodes a (debounced) city/postal-code + country to coordinates for the
 * radius map. Coordinates are stable, so results are cached indefinitely.
 *
 * @param {string} location - City or postal code (already debounced by the caller)
 * @param {string} country - Country name (biases the lookup)
 * @param {boolean} enabled - Whether to run the lookup
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export function useGeocode(location, country, enabled) {
	return useQuery({
		queryKey: prospectQueryKeys.geocode(location, country),
		queryFn: () => geocodeLocation({ location, country }),
		enabled: Boolean(enabled && location && location.trim().length >= 2),
		staleTime: STALE_TIMES.STATIC,
		meta: { suppressGlobalError: true },
	});
}
